import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { CircleNotchIcon, MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { ApiError, semanticClassApi, transactionsApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { Transaction } from '@/types';
import type { ClassTotals, SemanticClassWire, StreamCandidate, StreamTransaction } from '@/types/onboarding';
import {
  countedSummary,
  groupByMonth,
  missingCandidatesFor,
  selectionStats,
  shortDate,
  withExplicitPlus,
  type CountedMonth,
  type StreamsState,
} from './streams.helpers';

interface TotalClassRow {
  class: Extract<SemanticClassWire, 'income' | 'cardPayment' | 'investment'>;
  label: string;
  sub: string;
  action: string;
}

// "em média" is not decoration: the figure is an average over every month the
// class reached, and calling it "o que entra todo mês" asserted a certainty the
// rows underneath never back up.
const TOTAL_CLASSES: TotalClassRow[] = [
  { class: 'income', label: 'Renda', sub: 'o que entra, em média, por mês', action: 'É renda' },
  { class: 'cardPayment', label: 'Pagamentos de fatura', sub: 'o que sai, em média, por mês', action: 'É fatura' },
  { class: 'investment', label: 'Investimentos', sub: 'o que sai, em média, por mês', action: 'É investimento' },
];

export interface TotalsCheckCardProps {
  totals: ClassTotals | undefined;
  reachedRows: number;
  state: StreamsState;
  deferredCount: number;
  /** How many rows the user claimed through the review panel (streams or search). */
  declaredCount: number;
  onCheckTotal: (cls: SemanticClassWire) => void;
  onOpenMissing: (cls: SemanticClassWire) => void;
  onCloseMissing: () => void;
  /** One-click claim of an unanswered stream from the review panel. */
  onClaimStream: (stream: StreamCandidate, cls: SemanticClassWire) => void;
  /** A transaction claimed via the search fallback was written server-side. */
  onClaimedTransaction: () => void;
  /** "Isso não está certo aqui": removes one counted row from the class — the
   * parent writes the semantic re-decision and refetches. */
  onDeselectCounted: (transactionId: string, cls: SemanticClassWire) => void;
}

export function TotalsCheckCard(props: TotalsCheckCardProps) {
  const { totals, state } = props;
  // "If a group is empty it does not need to be displayed" (Davi, 2026-08-20).
  const visible = TOTAL_CLASSES.filter((c) => (totals?.[c.class]?.rowCount ?? 0) > 0);
  // state.missingFor doubles as "which class row is expanded for review".
  const expandedClass = visible.find((c) => c.class === state.missingFor) ?? null;
  const missingStreams = useMemo(
    () => (expandedClass ? missingCandidatesFor(state, expandedClass.class) : []),
    [expandedClass, state],
  );

  return (
    // min-w-0: grid/flex-child guard — see StreamCard.
    <Card className="min-w-0 p-5">
      <p className="font-mono text-2xs tracking-[0.14em] text-text-tertiary uppercase">
        Confira os totais
      </p>

      {visible.length === 0 && (
        <p className="mt-2 text-xs text-text-tertiary">
          Os totais aparecem conforme os grupos acima são respondidos.
        </p>
      )}

      <ul className="mt-2">
        {visible.map((row) => (
          <TotalRow
            key={row.class}
            row={row}
            totals={totals}
            checked={state.checkedTotals.includes(row.class)}
            expanded={expandedClass?.class === row.class}
            onCheckTotal={props.onCheckTotal}
            onOpenMissing={props.onOpenMissing}
            onCloseMissing={props.onCloseMissing}
          >
            {expandedClass?.class === row.class && (
              <div className="mt-1 mb-2 rounded-xl bg-surface-sunken p-3">
                <CountedRows
                  cls={row.class}
                  label={row.label}
                  rowCount={totals?.[row.class]?.rowCount ?? 0}
                  transactions={totals?.[row.class]?.transactions ?? []}
                  onDeselectCounted={props.onDeselectCounted}
                />

                {missingStreams.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-text-secondary">
                      Faltou algo? Estes grupos ainda não têm resposta:
                    </p>
                    <ul className="mt-2 flex flex-col gap-2">
                      {missingStreams.map((stream) => (
                        <MissingStreamRow
                          key={stream.streamKey}
                          stream={stream}
                          excludedIds={state.excluded[stream.streamKey] ?? []}
                          cls={row.class}
                          action={row.action}
                          onClaimStream={props.onClaimStream}
                        />
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-3">
                  <p className="text-xs font-semibold text-text-secondary">
                    Procurar outro lançamento:
                  </p>
                  <MissingSearch
                    key={row.class}
                    cls={row.class}
                    action={row.action}
                    onClaimedTransaction={props.onClaimedTransaction}
                  />
                </div>
              </div>
            )}
          </TotalRow>
        ))}
      </ul>

      <div className="mt-3.5 flex flex-wrap gap-3">
        <p className="text-[11px] text-text-tertiary">
          As perguntas acima cobrem{' '}
          <span className="font-mono font-bold tabular-nums text-text-primary">{props.reachedRows}</span>{' '}
          {props.reachedRows === 1 ? 'lançamento' : 'lançamentos'}
        </p>
        {props.deferredCount > 0 && (
          <p className="text-[11px] text-text-tertiary">
            · {props.deferredCount === 1 ? '1 grupo ficou' : `${props.deferredCount} grupos ficaram`} para depois
          </p>
        )}
        {props.declaredCount > 0 && (
          <p className="text-[11px] text-success-text">
            · você achou {props.declaredCount === 1 ? '1 grupo' : `${props.declaredCount} grupos`} que não sugerimos
          </p>
        )}
      </div>
    </Card>
  );
}

function TotalRow({
  row,
  totals,
  checked,
  expanded,
  onCheckTotal,
  onOpenMissing,
  onCloseMissing,
  children,
}: {
  row: TotalClassRow;
  totals: ClassTotals | undefined;
  checked: boolean;
  expanded: boolean;
  onCheckTotal: (cls: SemanticClassWire) => void;
  onOpenMissing: (cls: SemanticClassWire) => void;
  onCloseMissing: () => void;
  children?: React.ReactNode;
}) {
  const formatCurrency = useFormatCurrency();
  const handleConfirm = useCallback(() => onCheckTotal(row.class), [onCheckTotal, row.class]);
  const handleToggleReview = useCallback(() => {
    if (expanded) onCloseMissing();
    else onOpenMissing(row.class);
  }, [expanded, onCloseMissing, onOpenMissing, row.class]);

  const total = totals?.[row.class];
  // Renda enters (+), fatura and investimento leave (−) — explicit sign on both sides.
  const value = row.class === 'income' ? (total?.monthlyAverage ?? 0) : -(total?.monthlyAverage ?? 0);

  return (
    <li className="border-b border-divider">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5">
        {/* basis-40 keeps the label from being squeezed to a one-word column at
         * 360px — the amount + button cluster wraps to its own line instead. */}
        <span className="min-w-0 flex-1 basis-40">
          <span className="block text-sm font-medium">{row.label}</span>
          <span className="block text-xs text-text-secondary">{row.sub}</span>
        </span>
        <span
          className={cn(
            'shrink-0 font-mono text-sm tabular-nums',
            row.class === 'income' ? 'text-amount-positive' : 'text-amount-negative',
          )}
        >
          <MoneyValue text={withExplicitPlus(formatCurrency(value), value)} />
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-1.5">
          {checked && <Badge variant="success">Conferido</Badge>}
          {!checked && (
            <Button variant="outline" size="sm" onClick={handleConfirm}>
              Está certo
            </Button>
          )}
          <Button variant="ghost" size="sm" aria-expanded={expanded} onClick={handleToggleReview}>
            {expanded ? 'Fechar' : 'Revisar'}
          </Button>
        </span>
      </div>
      {children}
    </li>
  );
}

/** The rows the figure counts, each removable — "isso não está certo aqui" writes
 * a semantic re-decision through the parent. */
function CountedRows({
  cls,
  label,
  rowCount,
  transactions,
  onDeselectCounted,
}: {
  cls: SemanticClassWire;
  label: string;
  rowCount: number;
  transactions: StreamTransaction[];
  onDeselectCounted: (transactionId: string, cls: SemanticClassWire) => void;
}) {
  const months = useMemo(() => groupByMonth(transactions), [transactions]);
  if (rowCount === 0) return null;

  return (
    <div>
      <p className="text-xs font-semibold text-text-secondary">
        Contado como {label.toLowerCase()} — desmarque o que não pertence:
      </p>
      <CountedRowsUnavailable rowCount={rowCount} shown={transactions.length} />
      {months.length > 0 && (
        <>
          <p className="mt-1 text-[11px] text-text-tertiary">
            {countedSummary(transactions.length, rowCount, months.length)}
          </p>
          {/* Bounded: a class with 50 rows rendered flat pushed the other two
              totals — and Continuar — thousands of pixels down the page. */}
          <div
            className="mt-1.5 max-h-80 overflow-y-auto"
            tabIndex={0}
            role="group"
            aria-label={`Lançamentos contados como ${label.toLowerCase()}`}
          >
            {months.map((month) => (
              <CountedMonthGroup
                key={month.key}
                month={month}
                cls={cls}
                label={label}
                onDeselectCounted={onDeselectCounted}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** The deployed API may still serve a total without its rows. Opening "Revisar"
 * on to an empty panel reads as a broken screen, so name what is missing. */
function CountedRowsUnavailable({ rowCount, shown }: { rowCount: number; shown: number }) {
  if (shown > 0) return null;
  return (
    <p className="mt-2 rounded-lg bg-surface px-2.5 py-2 text-[11px] text-text-tertiary">
      Não foi possível carregar os {rowCount} lançamentos deste total agora. Você pode revisar
      todos eles depois, dentro do app.
    </p>
  );
}

function CountedMonthGroup({
  month,
  cls,
  label,
  onDeselectCounted,
}: {
  month: CountedMonth;
  cls: SemanticClassWire;
  label: string;
  onDeselectCounted: (transactionId: string, cls: SemanticClassWire) => void;
}) {
  const formatCurrency = useFormatCurrency();

  return (
    <section>
      {/* Sticky so the month a row belongs to survives scrolling the region. */}
      <header className="sticky top-0 z-10 flex items-baseline justify-between gap-3 bg-surface-sunken py-1">
        <span className="text-[11px] font-semibold text-text-secondary">{month.label}</span>
        <span
          className={cn(
            'shrink-0 font-mono text-[11px] tabular-nums',
            month.subtotal > 0 ? 'text-amount-positive' : 'text-amount-negative',
          )}
        >
          <MoneyValue text={withExplicitPlus(formatCurrency(month.subtotal), month.subtotal)} />
        </span>
      </header>
      <ul className="flex flex-col gap-1.5 pb-2">
        {month.transactions.map((tx) => (
          <CountedRow
            key={tx.id}
            tx={tx}
            cls={cls}
            label={label}
            onDeselectCounted={onDeselectCounted}
          />
        ))}
      </ul>
    </section>
  );
}

function CountedRow({
  tx,
  cls,
  label,
  onDeselectCounted,
}: {
  tx: StreamTransaction;
  cls: SemanticClassWire;
  label: string;
  onDeselectCounted: (transactionId: string, cls: SemanticClassWire) => void;
}) {
  const formatCurrency = useFormatCurrency();
  const handleDeselect = useCallback(
    () => onDeselectCounted(tx.id, cls),
    [onDeselectCounted, tx.id, cls],
  );

  return (
    <li className="flex min-w-0 items-center gap-2.5 rounded-lg bg-surface px-2.5 py-1.5">
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs text-text-primary">{tx.description}</span>
        <span className="block font-mono text-[11px] tabular-nums text-text-tertiary">
          {/* dd/mm: the month header above carries the month and year. */}
          {shortDate(tx.date)} ·{' '}
          <span className={tx.amount > 0 ? 'text-amount-positive' : 'text-amount-negative'}>
            {withExplicitPlus(formatCurrency(tx.amount), tx.amount)}
          </span>
        </span>
      </span>
      <button
        type="button"
        aria-label={`Remover ${tx.description} de ${label}`}
        onClick={handleDeselect}
        className="-my-1 shrink-0 cursor-pointer rounded-md p-2 text-text-tertiary hover:bg-surface-sunken hover:text-text-primary"
      >
        <XIcon size={13} weight="bold" />
      </button>
    </li>
  );
}

function MissingStreamRow({
  stream,
  excludedIds,
  cls,
  action,
  onClaimStream,
}: {
  stream: StreamCandidate;
  /** The selection made on the stream's main card — the claim posts exactly it,
   * so the row must show that value and refuse a zero-selection claim. */
  excludedIds: string[];
  cls: SemanticClassWire;
  action: string;
  onClaimStream: (stream: StreamCandidate, cls: SemanticClassWire) => void;
}) {
  const formatCurrency = useFormatCurrency();
  const handleClaim = useCallback(() => onClaimStream(stream, cls), [onClaimStream, stream, cls]);
  const stats = selectionStats(stream.transactions, excludedIds);
  const noneSelected = stats.selected.length === 0;

  return (
    <li className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-border-subtle px-3 py-2.5">
      <span className="min-w-0 flex-1 basis-40">
        <span className="block truncate font-mono text-[11px] text-text-primary">{stream.displayLabel}</span>
        <span className="block text-xs text-text-secondary">
          {noneSelected
            ? 'nenhum lançamento selecionado — selecione no grupo acima'
            : `${stats.selected.length} de ${stats.total} lançamentos · ${withExplicitPlus(formatCurrency(stats.monthly), stats.monthly)} por mês`}
        </span>
      </span>
      <span className="ml-auto shrink-0">
        <Button variant="outline" size="sm" disabled={noneSelected} onClick={handleClaim}>
          {action}
        </Button>
      </span>
    </li>
  );
}

/**
 * The search fallback ("Procurar outro lançamento"): when what's missing isn't
 * among the suggested groups, find the row in the user's own transactions and
 * record the semantic decision directly (PATCH /transactions/semantic-class).
 */
function MissingSearch({
  cls,
  action,
  onClaimedTransaction,
}: {
  cls: SemanticClassWire;
  action: string;
  onClaimedTransaction: () => void;
}) {
  const authedFetch = useAuthedFetcher();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Transaction[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [claimedIds, setClaimedIds] = useState<string[]>([]);

  const handleQueryChange = useCallback((event: FormEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setQuery(value);
    // Emptying the input clears stale results — no ghost list under a blank query.
    if (value.trim().length === 0) setResults(null);
  }, []);

  const handleClear = useCallback(() => {
    setQuery('');
    setResults(null);
  }, []);

  const handleSearch = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = query.trim();
    if (q.length < 2) return;
    setSearching(true);
    try {
      const page = await authedFetch((token) => transactionsApi.getAll(token, { q, limit: 12 }));
      // Only rows the class can actually claim: income is an inflow answer, fatura
      // and investimento are outflow answers — offering "É renda" on a DEBIT row is
      // nonsense the backend's direction gate would refuse anyway.
      const wantOutflow = cls !== 'income';
      setResults(page.items.filter((tx) => (tx.type === 'DEBIT') === wantOutflow).slice(0, 8));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível buscar os lançamentos.');
    } finally {
      setSearching(false);
    }
  }, [authedFetch, cls, query]);

  const handleClaim = useCallback(
    async (transactionId: string) => {
      try {
        await authedFetch((token) => semanticClassApi.setBatch(token, [transactionId], cls));
        setClaimedIds((current) => [...current, transactionId]);
        onClaimedTransaction();
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Não foi possível classificar o lançamento.');
      }
    },
    [authedFetch, cls, onClaimedTransaction],
  );

  return (
    <div className="mt-3">
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Input
            value={query}
            onInput={handleQueryChange}
            placeholder="Ex.: salário, aluguel"
            aria-label="Procurar outro lançamento"
            // 16px on phones or iOS Safari zooms the page in on focus and never
            // zooms back out; the Input primitive's own text-base/md:text-sm rule
            // says the same thing, and a bare text-xs would override it.
            className="h-8 pr-8 text-base md:text-xs"
          />
          {query.length > 0 && (
            <button
              type="button"
              aria-label="Limpar busca"
              onClick={handleClear}
              className="absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer text-text-tertiary hover:text-text-primary"
            >
              <XIcon size={13} weight="bold" />
            </button>
          )}
        </div>
        <Button type="submit" variant="outline" size="sm" disabled={searching || query.trim().length < 2}>
          {searching ? <CircleNotchIcon size={13} className="animate-spin" /> : <MagnifyingGlassIcon size={13} weight="bold" />}
          Buscar
        </Button>
      </form>

      {results !== null && results.length === 0 && (
        <p className="mt-2 text-xs text-text-tertiary">Nenhum lançamento encontrado.</p>
      )}

      {results !== null && results.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1.5">
          {results.map((tx) => (
            <SearchResultRow
              key={tx.id}
              tx={tx}
              action={action}
              claimed={claimedIds.includes(tx.id)}
              onClaim={handleClaim}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function SearchResultRow({
  tx,
  action,
  claimed,
  onClaim,
}: {
  tx: Transaction;
  action: string;
  claimed: boolean;
  onClaim: (transactionId: string) => void;
}) {
  const formatCurrency = useFormatCurrency();
  const handleClaim = useCallback(() => onClaim(tx.id), [onClaim, tx.id]);

  return (
    <li className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-border-subtle px-3 py-2">
      <span className="min-w-0 flex-1 basis-40">
        <span className="block truncate text-xs text-text-primary">{tx.description}</span>
        <span className="block font-mono text-[11px] tabular-nums text-text-tertiary">
          {new Date(tx.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} ·{' '}
          <span className={tx.amount > 0 ? 'text-amount-positive' : 'text-amount-negative'}>
            {withExplicitPlus(formatCurrency(tx.amount), tx.amount)}
          </span>
        </span>
      </span>
      <span className="ml-auto shrink-0">
        {claimed ? (
          <Badge variant="success">Incluído</Badge>
        ) : (
          <Button variant="outline" size="sm" onClick={handleClaim}>
            {action}
          </Button>
        )}
      </span>
    </li>
  );
}
